import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Eye, KeyRound, RefreshCw, Search, ShieldMinus, ShieldPlus, Snowflake, Trash2, UserRoundCheck, WalletCards } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { AdminUser, LedgerEntry, Role, UserStatus } from '../types';
import { formatMoney, formatTime, ledgerKindLabel, roleLabel, statusLabel } from '../utils';
import { Badge, Button, EmptyBlock, LoadingBlock, PageHeader, Panel, Select, TextInput } from '../components/ui';

export function MembersPage() {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<AdminUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [ledgerUser, setLedgerUser] = useState<AdminUser | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[] | null>(null);
  const [bindings, setBindings] = useState<Record<string, any>>({});
  const ledgerRef = useRef<HTMLDivElement | null>(null);

  // 点「流水」后把结果面板滚进视野，否则它出现在页面最底部，看起来像没反应
  useEffect(() => {
    if (!ledgerUser) return;
    const timer = window.setTimeout(() => ledgerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    return () => window.clearTimeout(timer);
  }, [ledgerUser, ledger]);

  const load = async () => {
    if (!permissions.isStaff) return;
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    const data = await api<{ users: AdminUser[] }>('/api/admin/users?' + params.toString());
    setItems(data.users);
    const binds = await api<{ bindings: any[] }>('/api/v2/admin/bindings').catch(() => ({ bindings: [] }));
    const map: Record<string, any> = {};
    for (const binding of binds.bindings) map[binding.userId] = binding;
    setBindings(map);
  };

  useEffect(() => {
    void load().catch(() => toast('成员列表加载失败', 'error'));
  }, [permissions.isStaff, user?.id]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    void load().catch(() => toast('查询失败', 'error'));
  };

  const run = async (target: AdminUser, task: () => Promise<void>, success: string) => {
    try {
      await task();
      await load();
      toast(success, 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '操作失败', 'error');
    }
  };

  const viewPassword = (target: AdminUser) => run(target, async () => {
    const data = await api<{ password: string }>('/api/admin/users/' + target.id + '/password');
    setRevealed(current => ({ ...current, [target.id]: data.password }));
  }, '密码已显示，查看行为已记录');

  const resetPassword = async (target: AdminUser) => {
    const approved = await confirm({ title: '重置密码', body: '将随机生成新密码并立即注销该账号的所有会话。', danger: true, confirmText: '确认重置' });
    if (!approved) return;
    await run(target, async () => {
      const data = await api<{ password: string }>('/api/admin/users/' + target.id + '/reset-password', { method: 'POST', body: JSON.stringify({}) });
      setRevealed(current => ({ ...current, [target.id]: data.password }));
    }, '密码已重置');
  };

  const adjustBalance = async (target: AdminUser) => {
    const amountText = window.prompt('输入余额变动金额（正数增加，负数扣减）\n当前余额：' + formatMoney(target.balanceCents, settings.currency), '100');
    if (amountText === null) return;
    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount === 0) return toast('金额格式不正确', 'error');
    const reason = window.prompt('请输入变动原因（至少 2 个字）', '管理员调整');
    if (!reason || reason.trim().length < 2) return;
    await run(target, async () => {
      await api('/api/admin/users/' + target.id + '/balance', { method: 'POST', body: JSON.stringify({ deltaCents: Math.round(amount * 100), reason: reason.trim() }) });
    }, '余额已更新');
  };

  const setUserStatus = async (target: AdminUser, nextStatus: UserStatus) => {
    const actionName = nextStatus === 'active' ? '解除冻结' : nextStatus === 'frozen' ? '冻结账号' : '注销账号';
    const reason = window.prompt('请输入' + actionName + '原因（至少 2 个字）', actionName);
    if (!reason || reason.trim().length < 2) return;
    const approved = await confirm({ title: actionName, body: nextStatus === 'deleted' ? '注销后账号不能恢复，历史记录仍会保留。' : '账号状态会立即生效。', danger: nextStatus !== 'active', confirmText: '确认' });
    if (!approved) return;
    await run(target, async () => {
      await api('/api/admin/users/' + target.id + '/status', { method: 'PATCH', body: JSON.stringify({ status: nextStatus, reason: reason.trim() }) });
    }, actionName + '成功');
  };

  const changeRole = async (target: AdminUser, nextRole: Role) => {
    const appoint = nextRole === 'admin';
    const approved = await confirm({ title: appoint ? '任命管理员' : '撤销管理员', body: appoint ? '该成员将获得公告、成员、设置和兑换码管理权限。' : '该账号将降级为普通成员。', danger: !appoint, confirmText: '确认' });
    if (!approved) return;
    await run(target, async () => {
      await api('/api/admin/users/' + target.id + '/role', { method: 'PATCH', body: JSON.stringify({ role: nextRole }) });
      if (target.id === user?.id) await refresh();
    }, appoint ? '已任命为管理员' : '已撤销管理员');
  };

  const showLedger = async (target: AdminUser) => {
    setLedgerUser(target);
    setLedger(null);
    try {
      const data = await api<{ entries: LedgerEntry[] }>('/api/admin/users/' + target.id + '/ledger');
      setLedger(data.entries);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '流水加载失败', 'error');
    }
  };

  const unbind = async (target: AdminUser, binding: any) => {
    const approved = await confirm({ title: '解绑游戏角色', body: '解绑「' + (binding.gameName || '未填写') + '」后，该玩家需要重新在游戏里绑定才能继续用网站余额和购物。', danger: true, confirmText: '确认解绑' });
    if (!approved) return;
    await run(target, async () => { await api('/api/v2/admin/bindings/' + binding.id, { method: 'DELETE' }); }, '已解绑');
  };

  if (!permissions.isStaff) return <EmptyBlock title="无权访问" description="成员管理仅对管理员开放。" />;

  return (
    <>
      <PageHeader title="成员管理" description="账号、余额、状态、密码、角色绑定都在这里管；管理员只能管普通成员，超级管理员可以管管理员账号。" action={<Button onClick={() => void load()}><RefreshCw size={16} /> 刷新</Button>} />
      <Panel className="bottom-gap">
        <form className="filter-bar" onSubmit={search}>
          <label><Search size={16} /><TextInput value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索游戏 ID 或显示名称" /></label>
          <Select value={role} onChange={event => setRole(event.target.value)}>
            <option value="">全部角色</option><option value="member">成员</option>{permissions.isSuperadmin && <option value="admin">管理员</option>}{permissions.isSuperadmin && <option value="superadmin">超级管理员</option>}
          </Select>
          <Select value={status} onChange={event => setStatus(event.target.value)}>
            <option value="">全部状态</option><option value="active">正常</option><option value="frozen">已冻结</option><option value="deleted">已注销</option>
          </Select>
          <Button variant="primary" type="submit">查询</Button>
        </form>
      </Panel>

      {ledgerUser && (
        <div ref={ledgerRef} className="ledger-focus">
        <Panel title={ledgerUser.username + ' 的余额流水'} eyebrow="LEDGER" className="bottom-gap" action={<Button variant="ghost" onClick={() => setLedgerUser(null)}>关闭</Button>}>
          {!ledger ? <LoadingBlock /> : ledger.length === 0 ? <EmptyBlock title="暂无流水" /> : (
            <div className="ledger-list">
              {ledger.map(entry => (
                <div key={entry.id}>
                  <span className={entry.deltaCents >= 0 ? 'positive' : 'negative'}>{entry.deltaCents >= 0 ? '+' : ''}{formatMoney(entry.deltaCents, settings.currency)}</span>
                  <strong>{entry.note}</strong>
                  <small>{ledgerKindLabel[entry.kind] || entry.kind} · 余额 {formatMoney(entry.balanceAfterCents, settings.currency)} · {formatTime(entry.createdAt)}</small>
                </div>
              ))}
            </div>
          )}
        </Panel>
        </div>
      )}
      <Panel title={'账号列表' + (items ? ' · ' + items.length : '')} eyebrow="ACCOUNT REGISTER">
        {!items ? <LoadingBlock /> : items.length === 0 ? <EmptyBlock title="没有符合条件的账号" /> : (
          <div className="member-list">
            {items.map(item => (
              <article className={'member-row ' + (item.status !== 'active' ? 'inactive' : '')} key={item.id}>
                <div className="member-identity">
                  <span className="member-avatar">{item.username.slice(0, 1).toUpperCase()}</span>
                  <div><strong>{item.displayName}</strong><small>@{item.username} · {roleLabel[item.role]}</small></div>
                </div>
                <div className="member-balance"><span>余额</span><strong>{formatMoney(item.balanceCents, settings.currency)}</strong></div>
                <div className="member-binding">{bindings[item.id] ? (bindings[item.id].status === 'active' ? <><Badge tone="good">已绑定</Badge><small>{bindings[item.id].gameName || '—'} · XUID {bindings[item.id].xuid || '—'}{bindings[item.id].boundAt ? ' · ' + formatTime(bindings[item.id].boundAt) : ''}</small></> : <><Badge tone="warn">待绑定</Badge><small>已生成绑定码，还没在游戏里发</small></>) : <><Badge tone="muted">未绑定</Badge><small>没绑角色，用不了余额</small></>}</div>
                <div className="member-status"><Badge tone={item.status === 'active' ? 'good' : item.status === 'frozen' ? 'warn' : 'danger'}>{statusLabel[item.status]}</Badge><small>{item.lastLoginAt ? '上次登录 ' + formatTime(item.lastLoginAt) : '从未登录'}</small></div>
                <div className="member-actions">
                  {item.permissions.balance && <Button variant="ghost" onClick={() => void adjustBalance(item)}><WalletCards size={15} /> 余额</Button>}
                  {item.permissions.password && <Button variant="ghost" onClick={() => void viewPassword(item)}><Eye size={15} /> 看密码</Button>}
                  {item.permissions.resetPassword && <Button variant="ghost" onClick={() => void resetPassword(item)}><KeyRound size={15} /> 重置</Button>}
                  <Button variant="ghost" onClick={() => void showLedger(item)}>流水</Button>
                  {bindings[item.id] && <Button variant="danger" onClick={() => void unbind(item, bindings[item.id])}>解绑角色</Button>}
                  {item.permissions.role && <Button variant="ghost" onClick={() => void changeRole(item, item.role === 'admin' ? 'member' : 'admin')}>{item.role === 'admin' ? <ShieldMinus size={15} /> : <ShieldPlus size={15} />}{item.role === 'admin' ? '撤管' : '任命'}</Button>}
                  {item.permissions.status && item.status === 'active' && <Button variant="ghost" onClick={() => void setUserStatus(item, 'frozen')}><Snowflake size={15} /> 冻结</Button>}
                  {item.permissions.status && item.status === 'frozen' && <Button variant="ghost" onClick={() => void setUserStatus(item, 'active')}><UserRoundCheck size={15} /> 解冻</Button>}
                  {item.permissions.status && item.status !== 'deleted' && <Button variant="danger" onClick={() => void setUserStatus(item, 'deleted')}><Trash2 size={15} /> 注销</Button>}
                </div>
                {revealed[item.id] && <div className="password-reveal"><KeyRound size={15} /><span>当前密码：</span><code>{revealed[item.id]}</code><button onClick={() => setRevealed(current => ({ ...current, [item.id]: '' }))}>隐藏</button></div>}
              </article>
            ))}
          </div>
        )}
      </Panel>

    </>
  );
}
